import { ConsumerStudentPayment } from '@givebacks/api/student';
import { centsToDollar } from '@givebacks/utils/currency';
import { PAYMENT_METHODS_MAPPING, PaymentMethods } from 'core/Utils';
import { DateTime } from 'luxon';
import { Group, Stack, Text } from 'ui';

type PaymentHistoryProps = {
  payments: ConsumerStudentPayment[];
  feeTotal: number;
  amountPaid: number;
  remainingBalance: number;
};

type PaymentGroupProps = {
  label: React.ReactNode;
  value: number;
  showNegative?: boolean;
  isRefunded?: boolean;
};

const getPaymentMethodText = (paymentMethod?: PaymentMethods, status?: string) => {
  // If the status is refunded, return "Refund"
  if (status === 'refunded') {
    return '(Refund)';
  }

  // If the payment method is not in the PAYMENT_METHODS_MAPPING, return an empty string
  if (paymentMethod && paymentMethod in PAYMENT_METHODS_MAPPING) {
    const method = PAYMENT_METHODS_MAPPING[paymentMethod];
    return `(${method})`;
  }
  return '';
};

const PaymentGroup = ({
  label,
  value,
  showNegative = false,
  isRefunded = false,
}: PaymentGroupProps) => {
  return (
    <Group justify="space-between" c={isRefunded ? 'cerise.6' : 'inherit'}>
      <Text>{label}</Text>
      <Text fw={600}>
        {showNegative ? '-' : ''} {centsToDollar(value)}
      </Text>
    </Group>
  );
};

const PaymentHistory = ({
  payments,
  feeTotal,
  amountPaid,
  remainingBalance,
}: PaymentHistoryProps) => {
  return (
    <Stack gap={4}>
      <PaymentGroup label={<Text fw={600}> Fee Total</Text>} value={feeTotal} />
      {payments?.map((payment) => (
        <PaymentGroup
          showNegative={payment.status !== 'refunded'}
          key={payment.uuid}
          label={`${DateTime.fromISO(payment.paid_at).toFormat(
            'MM/dd/yyyy'
          )} ${getPaymentMethodText(payment.payment_method, payment.status)}`}
          value={payment.amount}
          isRefunded={payment.status === 'refunded'}
        />
      ))}
      <PaymentGroup label={<Text fw={600}> Total Paid</Text>} value={amountPaid} />
      <PaymentGroup label={<Text fw={600}> Remaining Balance</Text>} value={remainingBalance} />
    </Stack>
  );
};

export { PaymentHistory };
